/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import PaperedTable from '../../../components/Table/PaperedTable';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { makeStyles, TableCell, TableHead } from '@material-ui/core';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import { Auth } from '../../../modules/auth/Auth';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ApiCollectionInterface } from '../../../interfaces/collections/ApiCollectionInterface';
import { ApnNameInterface, ApnNamePutInterface } from '../../../interfaces/collections/ApnNameInterface';
import { PlatformResellerAddApnName } from './PlatformResellerAddApnName';
import { configDefault } from '../../../core/config';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import TableRow from '@material-ui/core/TableRow';
import EditButton from '../../../components/Buttons/EditButton';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { PlatformResellerEditApnName } from './PlatformResellerEditApnName';
import ConfirmDialog from '../../../components/Dialog/ConfirmDialog';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import I18n from '../../../components/Translate/I18n';

const useStyles = makeStyles(adescomStyle);

type PlatformResellerApnPublicListType = {
    platformResellerIri: string;
};

export const PlatformResellerApnPublicList: FunctionComponent<PlatformResellerApnPublicListType> = ({ platformResellerIri }): ReactElement | null => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [platformReseller, setPlatformReseller] = useState<PlatformResellerInterface | null>(null);
    const [apnNameCollection, setApnNameCollection] = useState<ApiCollectionInterface<ApnNameInterface> | null>(null);
    const [openAddNew, setOpenAddNew] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [modelApnName, setModelApnName] = useState<ApnNameInterface | null>(null);

    useEffect(() => {
        if (platformReseller === null || platformReseller['@id'] !== platformResellerIri) {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getWithCallback(
                FormGeneralHelper.getByUri(platformResellerIri),
                'load-reseller-apn-manage',
                (data: PlatformResellerInterface): void => {
                    setPlatformReseller(data);
                    setApnNameCollection(null);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [platformResellerIri, platformReseller, dispatch]);

    useEffect(() => {
        if (platformReseller && apnNameCollection === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/apn_names', configDefault.PLATFORM_API_PREFIX)),
                'platform-reseller-apn-name-public-list',
                [
                    { name: 'isDeleted', value: false },
                    { name: 'isPublic', value: true },
                ],
                [{ name: 'apnName', value: 'asc' }],
                (data: ApiCollectionInterface<ApnNameInterface>): void => {
                    setApnNameCollection(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [platformReseller, dispatch, apnNameCollection]);

    const callDeleteApn = (): void => {
        if (modelApnName === null) {
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(modelApnName['@id']),
            'add-new-apn-name',
            {
                apnName: modelApnName.apnName,
                isDeleted: true,
            } as ApnNamePutInterface,
            {
                onSuccess: (data: ApnNameInterface): void => {
                    NotificationHelper.notifySaveSuccess(dispatch);
                    setOpenDelete(false);
                    setApnNameCollection(null);
                },
                onError: (data: {
                    message: {
                        message?: string | null;
                    };
                }): void => {
                    if (data.message && data.message.message) {
                        NotificationHelper.notifyError('pools:apn.' + data.message.message, dispatch);
                    }
                },
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    };

    const deleteApn = (apnName: ApnNameInterface): void => {
        setModelApnName(apnName);
        setOpenDelete(true);
    };
    const editApn = (apnName: ApnNameInterface): void => {
        setModelApnName(apnName);
        setOpenEdit(true);
    };

    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableRowRoot = { root: classes.tableRowRoot };

    if (platformReseller && platformReseller.parentReseller === null) {
        return (
            <Card style={{ marginBottom: '10px' }}>
                <CardHeader trans={'pools:apn.apn_public'} />
                <CardBody>
                    <GridContainer>
                        <GridItem xs={12} md={12} lg={12}>
                            <PaperedTable
                                beforeTableContent={
                                    <GridContainer>
                                        {Auth.isRootReseller() && (
                                            <GridItem xs={12} className={classes.beforeTableContent}>
                                                <CustomTextButtom
                                                    callback={(): void => setOpenAddNew(true)}
                                                    callbackParam={{}}
                                                    size={'sm'}
                                                    label="general:common.add_new"
                                                />
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                }
                            >
                                <TableHead>
                                    <TableRow classes={tableRowRoot}>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left" style={{ width: '100px' }}>
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="general:common.commands"
                                                showSort={false}
                                                onlyFilter={false}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                                            <TableHeaderContentCell textAlign="left" title="pools:apn.apn_name" showSort={false} onlyFilter={false} />
                                        </TableCell>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pools:apn.created_at"
                                                showSort={false}
                                                onlyFilter={false}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pools:apn.enable_without_ip"
                                                showSort={false}
                                                onlyFilter={false}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                {apnNameCollection &&
                                    apnNameCollection['hydra:member'].length > 0 &&
                                    apnNameCollection['hydra:member'].map((apnName) => {
                                        return (
                                            <TableRow classes={tableRowRoot} key={'key' + apnName.id} hover>
                                                <TableCell padding="none" align="left">
                                                    <EditButton
                                                        callback={editApn}
                                                        callbackParam={apnName}
                                                        tooltipTitle="pools:apn.edit_apn"
                                                        tooltipPlacement="bottom-start"
                                                    />
                                                    <DeleteButton
                                                        callback={deleteApn}
                                                        callbackParam={apnName}
                                                        tooltipTitle="pools:apn.delete_apn"
                                                        tooltipPlacement="bottom-start"
                                                    />
                                                </TableCell>
                                                <TableCell padding="none" align="left">
                                                    {apnName.apnName}
                                                </TableCell>
                                                <TableCell padding="none" align="left">
                                                    {DateHelperTs.format(apnName.createdAt)}
                                                </TableCell>
                                                <TableCell padding="none" align="left">
                                                    {<I18n trans={apnName.enableWithoutIp ? 'general:common.yes' : 'general:common.no'} />}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </PaperedTable>
                        </GridItem>
                    </GridContainer>
                    <PlatformResellerAddApnName
                        open={openAddNew}
                        platformResellerId={platformReseller.id}
                        reload={(): void => setApnNameCollection(null)}
                        closed={(): void => setOpenAddNew(false)}
                        isPublic={true}
                    />
                    {modelApnName && (
                        <>
                            <PlatformResellerEditApnName
                                open={openEdit}
                                apnName={modelApnName}
                                reload={(): void => setApnNameCollection(null)}
                                closed={(): void => setOpenEdit(false)}
                            />
                            <ConfirmDialog
                                title={'pools:apn.delete_apn'}
                                content={'pools:apn.confirm_delete_apn'}
                                extraContent={
                                    <>
                                        <GridContainer>
                                            <GridItem xs={12} style={{ textAlign: 'center' }}>
                                                {modelApnName.apnName}
                                            </GridItem>
                                        </GridContainer>
                                    </>
                                }
                                agreeButtonName={'pools:apn.confirm_ok'}
                                disagreeButtonName={'pools:apn.confirm_cancel'}
                                open={openDelete}
                                callBackAgree={callDeleteApn}
                                callBackDisAgree={(): void => setOpenDelete(false)}
                            />
                        </>
                    )}
                </CardBody>
            </Card>
        );
    }
    return null;
};
